import React, { useState } from 'react';
import useRoute from '@/Hooks/useRoute';
import FileNav from '@/Components/FileNav';
import { FileViewer } from './FileViewer';
import ButtonGroup from '@/Components/ButtonGroup';
import Button from '@/Components/Button'; // Base button component
import FavoriteButton from '@/Components/FavoriteButton';
import FileDownloadButton from '@/Components/FileDownloadButton';
import HoverableButton from '@/Components/HoverableButton';
import Download from '@/svgs/download.svg?react';
import IconDelete from '@/svgs/delete.svg?react';
import Star from '@/svgs/star.svg?react';
import IconEdit from '@/svgs/edit.svg?react';
import IconStars from '@/svgs/stars-lg.svg?react';
import IconTrash from '@/svgs/trash.svg?react';
import useTypedPage from '@/Hooks/useTypedPage';
import FileChangeName from './FileChangeName';
import { Tooltip } from '@/Components/Tooltip';
import { CursorPagination } from '@/types';
import ForkButton from '@/Components/ForkButton';
import Dialog from '@/Components/Dialog';
import { router } from '@inertiajs/react';

export interface SnippetViewerProps {
  files: App.Data.FileData[];
  file: App.Data.FileData;
  comments: CursorPagination<App.Data.CommentData>;
  package: App.Data.PackageData;
  modes: App.Data.ModeData[];
  canEdit: boolean;
  short_url: string;
}

export default function SnippetViewer({ file, files }: SnippetViewerProps) {
  const page = useTypedPage<SnippetViewerProps>();
  const route = useRoute();
  const [editting, setEditting] = useState(false);

  async function handleDeleteFile() {
    router.delete(
      route('files.destroy', { file: file.id }),
      {
        preserveState: false,
      }
    );
  }

  return (
    <div className="h-full">
      {file ? (
        <>
          <FileNav>
            <div className="flex items-center gap-1">
              <FileChangeName
                name={file.path}
                extension={file.extension}
                id={file.id}
              />
              <HoverableButton>
                <FileDownloadButton
                  className="hover:dark:text-brand-light-100"
                  file={file}
                >
                  <Tooltip message="Download the snippet.">
                    <span>
                      <Download className="h-5 w-5" />
                    </span>
                  </Tooltip>
                </FileDownloadButton>
              </HoverableButton>
              {page.props.auth.user?.id === page.props.file.owner?.id && (
                <>
                  <HoverableButton>
                    <FavoriteButton
                      url={route('files.favorite', file.id)}
                      favorited={file.favorited}
                      data={{ file_id: file.id }}
                      type={"file"}
                    >
                      <Star className="h-5 w-5" />
                    </FavoriteButton>
                  </HoverableButton>
                  <Dialog
                    title={'Confirm file deletion'}
                    description={'Are you sure you want to delete this file?'}
                    actionText={'Delete'}
                    actionHandler={handleDeleteFile}
                    trigger={
                      <Tooltip message="Delete File">
                        <HoverableButton className="hover:dark:bg-red-300 hover:dark:text-red-800">
                          <IconTrash className="h-4 w-4" />
                        </HoverableButton>
                      </Tooltip>
                    }
                  />
                </>
              )}
            </div>

            <ButtonGroup className="justify-end text-sm">
              {page.props.canEdit && (
                <>
                  <Button
                    className="gap-2 bg-brand-dark-25/50 dark:bg-brand-dark-75 dark:text-brand-light-25"
                    onClick={() => setEditting(!editting)}
                    type="button"
                  >
                    {editting ? (
                      <>
                        <IconDelete className="h-4 w-4" /> Cancel
                      </>
                    ) : (
                      <>
                        <IconEdit /> Edit Metadata
                      </>
                    )}
                  </Button>
                  <Button
                    styleType="primary"
                    href={route('files.show', file.id)}
                    forceUrlDomain="app"
                  >
                    <IconStars className="h-4 w-4" /> Generate changes
                  </Button>
                </>
              )}
              <HoverableButton>
                <ForkButton file={file} />
              </HoverableButton>
            </ButtonGroup>
          </FileNav>
          <FileViewer
            editting={editting}
            onMetaSave={() => setEditting(false)}
            file={file}
          />
        </>
      ) : (
        <div className="p-8 text-brand-light-25">
          <h1 className="text-4xl mb-12 font-bold">Select a file</h1>
          {files && (
            <ul className="space-y-4">
              {files.map(file => (
                <li key={file.id}>
                  <Button
                    href={route('snippet.show', file.id)}
                    forceUrlDomain="app"
                    className="block rounded-lg border border-brand-dark-20 px-3 py-2"
                  >
                    {file.path}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
